import { useCallback } from 'react';
import React from 'react';

import { Text } from '@rbilabs/universal-components';
import { usePaymentContext } from 'features/payment';
import { CardTypes, ICredit, IPaymentMethod } from 'features/payment/types';
import { mapPaymentMethodCardTypes } from 'features/payment/utils';
import { useIntl } from 'react-intl';

import { useUIContext } from 'state/ui';
import { theme } from 'styles/configure-theme';

import { PaymentIcon } from '../payment-redesign/payment-icon';

import { PaymentMethodDescription } from './payment-method-description';

export const PaymentMethod = ({ method }: { method: IPaymentMethod | null }) => {
  const { formatMessage } = useIntl();
  const { formatCurrencyForLocale } = useUIContext();
  const { getBalanceFromPaymentMethods } = usePaymentContext();
  const formatExpirationDate = useCallback(
    (credit: ICredit) => {
      return `${formatMessage({ id: 'expire' })} ${credit.expiryMonth}/${credit.expiryYear}`;
    },
    [formatMessage]
  );

  if (method?.credit) {
    const creditCardAlias = (
      <Text color={theme.token('text-default')}>
        {formatMessage({ id: 'endsInRedesign' }).toLowerCase()}
        {method?.credit.alias}
      </Text>
    );
    let isCreditCard = false;
    let alias: JSX.Element | string = creditCardAlias;
    let cardType;
    switch (method?.credit.cardType) {
      case CardTypes.APPLE_PAY:
        alias = formatMessage({ id: 'applePay' });
        break;
      case CardTypes.GOOGLE_PAY:
        alias = formatMessage({ id: 'googlePay' });
        break;
      default:
        cardType = mapPaymentMethodCardTypes(method?.credit.cardType);
        isCreditCard = true;
    }
    return (
      <>
        <PaymentIcon method={method} isMediumSize={true} />
        {isCreditCard && (
          <Text ml="$2" color={theme.token('text-default')} testID="payment-method-card">
            {cardType || formatMessage({ id: 'card' })}
          </Text>
        )}
        <Text ml="$2" color={theme.token('text-default')} textTransform="capitalize">
          {alias}
        </Text>
        {isCreditCard && (
          <Text
            color={theme.token('text-default')}
            testID="payment-method-expirationDate"
            textTransform="capitalize"
          >
            {` - ${formatExpirationDate(method.credit)}`}
          </Text>
        )}
      </>
    );
  }

  if (method?.prepaid) {
    const amount = getBalanceFromPaymentMethods(method);

    return (
      <>
        <PaymentIcon method={method} isMediumSize={true} />
        <PaymentMethodDescription
          methodName={formatMessage({ id: 'giftCard' })}
          amountDescription={<Text flex="1"> - {formatCurrencyForLocale(amount)}</Text>}
        />
      </>
    );
  }

  const methodTypes = {
    cash: formatMessage({ id: 'cash' }),
    paypal: formatMessage({ id: 'paypal' }),
    venmo: formatMessage({ id: 'venmo' }),
    cashapp: formatMessage({ id: 'cashAppPay' }),
  };

  for (const [key, value] of Object.entries(methodTypes)) {
    if (method?.[key]) {
      return (
        <>
          <PaymentIcon method={method} isMediumSize={true} />
          <PaymentMethodDescription methodName={value} />
        </>
      );
    }
  }

  return null;
};
